import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import ROUTES from '../../assets/routes'
import { matchRoute } from '../../util/routes'
import { selectDefaultLayoutId, selectUserDetails } from '../../selectors/user'
import { selectIsMobile } from '../../selectors/device'
import { Platform } from '../../proto/generated/user-preference'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import {
    AiChatEventMessage,
    AiChatWsMessage,
    AiConversationSummary,
    AiConversationTurnResponse,
    createConversation,
    getConversation,
    listConversations,
    updateTurnArtifactStatus,
} from '../../services/apis/aiChat'
import { WebSocketResponseMessage } from '../../proto/generated/websocket'
import {
    decodeAiChatFromWsFrame,
    isMainWsConnected,
    sendAiChatAbort,
    sendAiChatMessage,
    type AiChatRequestMetadata,
} from '../../services/apis/aiChatWebSocket'
import { ensureValidAccessToken } from '../../services/auth/tokenRefresh'
import {
    chatErrorCodeFromException,
    chatErrorText,
    chatErrorTextFromWire,
    reportChatError,
} from '../screener/aiChat/chatErrors'
import type { ChartCommandApplyResults, ChatTurnMessage, FilterCardStatus } from '../screener/aiChat/types'
import { resolveTurnCompletionDomain } from '../screener/aiChat/chatRunLifecycle'
import { AI_CHAT_PROGRESS_PLACEHOLDER } from '../screener/aiChat/progressStatusLabel'
import { parseFiltersFromProto } from '../screener/aiChat/conversationFilters'
import type { ScreenerFilterWithIdAndTag } from '../screener/StockScreener'
import {
    type AgentDomain,
    getReadyDomains,
    getSurface,
    getSurfaceToken,
    useSurfaceRegistry,
    waitForSurfaceReady,
    waitForSurfaceRegistered,
} from './surfaceRegistry'
import { routeDomain } from './routeDomain'
import {
    computePendingFilterUpdate,
    deriveTurnDomain,
    emptyScreenerLedgerState,
    filterStatusFromArtifacts,
    filtersFromArtifacts,
    applyServerLedger as applyScreenerServerLedger,
    type ScreenerLedgerState,
} from './domains/screenerDomain'
import { chartCommandsFromMetadata, chartCommandsFromNewMessages, targetChartIdFromMetadata } from './domains/chartDomain'
import {
    accountFundsInquiryFromArtifacts,
    markSupersededOrderPreviews,
    orderInquiryFromArtifacts,
    orderPreviewFromArtifacts,
    holdingsInquiryFromArtifacts,
    pnlInquiryFromArtifacts,
    tradeHistoryFromArtifacts,
    positionInquiryFromArtifacts,
    tradingTurnArtifactPatchFromMetadata,
    type OrderPreview,
    type TradingTurnArtifactPatch,
} from './domains/tradingDomain'
import type { ChartCommand } from '../chartV2/aiChat/chartCommands'
import {
    chartCommandFallbackContent,
    resolveChartCommandTurnContent,
} from '../chartV2/aiChat/chartCommandMessages'
import { registerAiAssistantOpen } from './aiAssistantBridge'
import { isAiAssistantEnabled } from './aiAssistantConfig'
import { readAiAssistantSession, writeAiAssistantSession, markAiAssistantScreenerScratchNav } from './aiAssistantSession'
import commonActions from '../../actions/common.actions'
import { WidgetListItems } from '../../util/sidebar'

export type AiAssistantOpenOptions = {
    domain?: AgentDomain
    seedPrompt?: string
    forceNew?: boolean
}

type AiAssistantContextValue = {
    conversationId: string | null
    messages: ChatTurnMessage[]
    input: string
    setInput: (value: string) => void
    loading: boolean
    error: string | null
    conversations: AiConversationSummary[]
    historyOpen: boolean
    setHistoryOpen: (open: boolean) => void
    wsConnected: boolean
    conversationDomain: AgentDomain | null
    onSend: (textOverride?: string, options?: { agent?: AiChatRequestMetadata['agent']; continuesTurn?: boolean }) => void
    onRetry: (prompt: string) => void
    onAbort: () => void
    onNewChat: () => void
    switchConversation: (id: string) => Promise<void>
    refreshConversations: () => Promise<void>
    openAssistant: (options?: AiAssistantOpenOptions) => void
    abortInFlight: () => void
    onUndoTurnFilters: (messageId: string) => void
    onOrderPreviewChange: (messageId: string, preview: OrderPreview) => void
    undoInFlight: boolean
    userName?: string
    userAvatarUrl?: string
    ledgerRef: React.MutableRefObject<ScreenerFilterWithIdAndTag[]>
}

const AiAssistantContext = createContext<AiAssistantContextValue | null>(null)

function isTerminalTurnStatus(status: string): boolean {
    const s = status.toLowerCase()
    return s === 'completed' || s === 'failed' || s === 'cancelled'
}

function isInFlightTurnStatus(status: string): boolean {
    const s = status.toLowerCase()
    return s === 'pending' || s === 'running'
}

function isRoutingOnlyAssistantText(text: string | undefined | null): boolean {
    const normalized = (text || '').trim().toLowerCase()
    return (
        normalized.startsWith('opening the chart') ||
        normalized.startsWith('opening the screener') ||
        normalized.includes('opening the chart') ||
        normalized.includes('opening the screener')
    )
}

function routingDomainFromStubText(text: string | undefined | null): AgentDomain | null {
    const normalized = (text || '').trim().toLowerCase()
    if (normalized.includes('screener')) return 'screener'
    if (normalized.includes('chart')) return 'chart'
    return null
}

function collapseNavigatedTurns(turns: AiConversationTurnResponse[]): AiConversationTurnResponse[] {
    const collapsed: AiConversationTurnResponse[] = []
    for (let i = 0; i < turns.length; i++) {
        const current = turns[i]
        const next = turns[i + 1]
        const currentIsRoutingStub =
            !current.assistantText?.trim() || isRoutingOnlyAssistantText(current.assistantText)
        if (
            next &&
            current.userQuery &&
            next.userQuery === current.userQuery &&
            currentIsRoutingStub &&
            next.assistantText?.trim() &&
            !isRoutingOnlyAssistantText(next.assistantText)
        ) {
            collapsed.push(next)
            i += 1
            continue
        }
        if (isRoutingOnlyAssistantText(current.assistantText)) {
            continue
        }
        collapsed.push(current)
    }
    return collapsed
}

export function AiAssistantProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const ws = useContext(WebSocketContext)
    const userDetails = useSelector(selectUserDetails)
    const isMobile = useSelector(selectIsMobile)
    const defaultLayoutId = useSelector(selectDefaultLayoutId(isMobile ? Platform.MOBILE : Platform.DESKTOP))
    useSurfaceRegistry()

    const initialSession = useMemo(() => readAiAssistantSession(), [])

    const [conversationId, setConversationId] = useState<string | null>(initialSession.conversationId)
    const [messages, setMessages] = useState<ChatTurnMessage[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)
    const [conversations, setConversations] = useState<AiConversationSummary[]>([])
    const [historyOpen, setHistoryOpen] = useState(false)
    const [wsConnected, setWsConnected] = useState(false)
    const [conversationDomain, setConversationDomain] = useState<AgentDomain | null>(null)
    const [undoInFlight, setUndoInFlight] = useState(false)
    const [seedPromptTick, setSeedPromptTick] = useState(0)

    const screenerLedgerRef = useRef<ScreenerLedgerState>(emptyScreenerLedgerState())
    const ledgerRef = useRef<ScreenerFilterWithIdAndTag[]>([])
    const messagesRef = useRef<ChatTurnMessage[]>([])
    const conversationIdRef = useRef<string | null>(conversationId)
    const activeTaskIdRef = useRef<string | null>(null)
    const ignoredTaskIdsRef = useRef<Set<string>>(new Set())
    const currentTurnIdRef = useRef<string | null>(null)
    const assistantIdRef = useRef<string | null>(null)
    const assistantHadDeltasRef = useRef(false)
    const lastSeqRef = useRef(0)
    const lastUserQueryRef = useRef<string | null>(null)
    const pendingCommandsRef = useRef<ChartCommand[]>([])
    const targetChartIdRef = useRef<string | null>(null)
    /** Set after CHART_COMMANDS_GENERATED apply so completion does not re-run the same ops. */
    const chartCommandsAppliedRef = useRef(false)
    const navigationTokenRef = useRef(0)
    const navAbortRef = useRef<AbortController | null>(null)
    const pendingNavigationRef = useRef<{ domain: AgentDomain; prompt: string } | null>(null)
    const undoInFlightRef = useRef(false)
    const loadingRef = useRef(false)
    const seedPromptRef = useRef<string | null>(null)
    const routingAssistantActiveRef = useRef(false)
    const pendingRunDomainRef = useRef<AgentDomain | null>(null)
    const rehydratedRef = useRef(false)
    const chartApplyInFlightRef = useRef<Promise<ChartCommandApplyResults | null> | null>(null)
    const runFinalizeWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const userName = userDetails?.currentUser?.name
    const userAvatarUrl = userDetails?.currentUser?.avatarUrl
    const assistantEnabled = isAiAssistantEnabled(userDetails)

    useEffect(() => {
        conversationIdRef.current = conversationId
    }, [conversationId])

    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    useEffect(() => {
        loadingRef.current = loading
    }, [loading])

    useEffect(() => {
        if (!assistantEnabled) return
        writeAiAssistantSession({
            conversationId,
            panelOpen: true,
        })
    }, [assistantEnabled, conversationId])

    useEffect(() => {
        setWsConnected(isMainWsConnected(ws))
        const socket = ws?.socket
        if (!socket) return
        const onOpen = () => setWsConnected(true)
        const onClose = () => setWsConnected(false)
        socket.addEventListener('open', onOpen)
        socket.addEventListener('close', onClose)
        return () => {
            socket.removeEventListener('open', onOpen)
            socket.removeEventListener('close', onClose)
        }
    }, [ws])

    const refreshConversations = useCallback(async () => {
        try {
            const list = await listConversations()
            setConversations(list)
        } catch {
            // non-critical
        }
    }, [])

    useEffect(() => {
        if (!assistantEnabled) return
        void refreshConversations()
    }, [assistantEnabled, refreshConversations])

    const cancelRunFinalizeWatchdog = useCallback(() => {
        if (runFinalizeWatchdogRef.current) {
            clearTimeout(runFinalizeWatchdogRef.current)
            runFinalizeWatchdogRef.current = null
        }
    }, [])

    const clearRunInFlightState = useCallback(() => {
        cancelRunFinalizeWatchdog()
        setLoading(false)
        setCurrentTaskId(null)
        activeTaskIdRef.current = null
    }, [cancelRunFinalizeWatchdog])

    const resetConversationUi = useCallback(() => {
        cancelRunFinalizeWatchdog()
        setMessages([])
        setError(null)
        setLoading(false)
        setCurrentTaskId(null)
        assistantIdRef.current = null
        assistantHadDeltasRef.current = false
        lastSeqRef.current = 0
        pendingCommandsRef.current = []
        targetChartIdRef.current = null
        chartCommandsAppliedRef.current = false
        lastUserQueryRef.current = null
        currentTurnIdRef.current = null
        activeTaskIdRef.current = null
        ignoredTaskIdsRef.current = new Set()
        screenerLedgerRef.current = emptyScreenerLedgerState()
        ledgerRef.current = []
    }, [cancelRunFinalizeWatchdog])

    const buildSurfaceMetadata = useCallback((): AiChatRequestMetadata['surface'] => {
        const route = routeDomain(location.pathname) ?? undefined
        const ready_domains = getReadyDomains()
        const surface_token =
            (route === 'chart' ? getSurfaceToken('chart') : route === 'screener' ? getSurfaceToken('screener') : undefined) ??
            getSurfaceToken('chart') ??
            getSurfaceToken('screener')
        return {
            route,
            ready_domains,
            ...(surface_token ? { surface_token } : {}),
        }
    }, [location.pathname])

    const buildRequestMetadata = useCallback(
        (agent: AiChatRequestMetadata['agent'], continuesTurn?: boolean): AiChatRequestMetadata => {
            const route = routeDomain(location.pathname)
            const metadata: AiChatRequestMetadata = {
                agent,
                surface: buildSurfaceMetadata(),
                conversation_domain:
                    conversationDomain ??
                    (route === 'chart' ? 'chart' : route === 'screener' ? 'screener' : undefined),
                continues_turn: continuesTurn || undefined,
            }
            // Always attach live chart snapshot when the chart surface is available.
            const chartSurface = getSurface('chart')
            if (chartSurface && (chartSurface.ready || route === 'chart')) {
                try {
                    const surfaceMeta = chartSurface.buildMetadata()
                    if (surfaceMeta.chart_context) {
                        metadata.chart_context = surfaceMeta.chart_context as AiChatRequestMetadata['chart_context']
                    }
                } catch (e) {
                    console.error('[AiAssistant] chart snapshot failed, sending without it', e)
                }
            }
            return metadata
        },
        [buildSurfaceMetadata, conversationDomain, location.pathname],
    )

    const navigateToDomain = useCallback(
        async (domain: AgentDomain): Promise<boolean> => {
            if (domain === 'chart') {
                if (!defaultLayoutId) return false
                navigate(generatePath(ROUTES.chart, { layoutId: defaultLayoutId }))
                return true
            }
            markAiAssistantScreenerScratchNav()
            navigate(generatePath(ROUTES.screener, { screenerId: '' }))
            return true
        },
        [defaultLayoutId, navigate],
    )

    const applyPendingChartCommands = useCallback(
        async (assistantMessageId?: string | null): Promise<ChartCommandApplyResults | null> => {
            while (chartApplyInFlightRef.current) {
                await chartApplyInFlightRef.current
            }

            const applyTask = (async (): Promise<ChartCommandApplyResults | null> => {
                const commands = pendingCommandsRef.current
                pendingCommandsRef.current = []
                const targetChartId = targetChartIdRef.current
                targetChartIdRef.current = null
                if (!commands.length) return null
                const surface = getSurface('chart')
                if (!surface) {
                    const failed: ChartCommandApplyResults = {
                        applied: [],
                        failed: [{ op: 'apply', message: chatErrorText('SURFACE_UNAVAILABLE') }],
                    }
                    if (assistantMessageId) {
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === assistantMessageId ? { ...m, chartCommandResults: failed, domain: 'chart' } : m,
                            ),
                        )
                    }
                    return failed
                }
                const artifactMetadata: Record<string, unknown> = { commands }
                if (targetChartId) {
                    artifactMetadata.target = { chart_id: targetChartId }
                }
                const results = (await surface.applyArtifacts(artifactMetadata)) as ChartCommandApplyResults | null
                if (results && (results.applied.length || results.failed.length)) {
                    chartCommandsAppliedRef.current = true
                }
                if (results?.failed.length) {
                    console.warn('[AiAssistant] chart commands failed', results)
                }
                if (assistantMessageId && results && (results.applied.length || results.failed.length)) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantMessageId ? { ...m, chartCommandResults: results, domain: 'chart' } : m,
                        ),
                    )
                }
                return results
            })()

            chartApplyInFlightRef.current = applyTask
            try {
                return await applyTask
            } finally {
                if (chartApplyInFlightRef.current === applyTask) {
                    chartApplyInFlightRef.current = null
                }
            }
        },
        [],
    )

    const mergeFinalReply = useCallback((currentContent: string, finalReply: string | undefined, seq?: number) => {
        const trimmed = finalReply?.trim()
        if (!trimmed) return currentContent
        if (typeof seq === 'number' && seq <= lastSeqRef.current && currentContent.length >= trimmed.length) {
            return currentContent
        }
        if (typeof seq === 'number') {
            lastSeqRef.current = Math.max(lastSeqRef.current, seq)
        }
        return trimmed.length >= currentContent.length ? trimmed : currentContent
    }, [])

    const finishChartRun = useCallback(
        async ({
            assistantMessageId,
            finalReply,
        }: {
            assistantMessageId: string | null
            finalReply: string | undefined
        }) => {
            cancelRunFinalizeWatchdog()
            try {
                const results = await applyPendingChartCommands(assistantMessageId)
                const existingResults =
                    assistantMessageId && !results?.applied.length && !results?.failed.length
                        ? messagesRef.current.find((m) => m.id === assistantMessageId)?.chartCommandResults
                        : undefined
                const effectiveResults = results ?? existingResults ?? null
                const retryPrompt = lastUserQueryRef.current || undefined
                let content = finalReply?.trim() || ''
                let showRetry = false
                let replaceContent = false
                const appliedCount = effectiveResults?.applied.length ?? 0

                if (!content && assistantMessageId) {
                    const existing = messagesRef.current.find((m) => m.id === assistantMessageId)
                    content = existing?.content?.trim() || ''
                }

                if (!content) {
                    if (appliedCount > 0) {
                        content = chartCommandFallbackContent(effectiveResults!)
                    } else if (!pendingCommandsRef.current.length && !chartCommandsAppliedRef.current) {
                        content = chatErrorText('NOT_UNDERSTOOD')
                        showRetry = true
                    }
                }
                const turnResolution = resolveChartCommandTurnContent(content, effectiveResults)
                if (turnResolution.replaceContent) {
                    content = turnResolution.content
                    replaceContent = true
                }
                if (turnResolution.showRetry) showRetry = true
                const chartCommandResults =
                    effectiveResults && (effectiveResults.applied.length || effectiveResults.failed.length)
                        ? effectiveResults
                        : undefined

                const patch = (entry: ChatTurnMessage): ChatTurnMessage => ({
                    ...entry,
                    // `mergeFinalReply` keeps whichever text is longer, which would preserve
                    // the agent's incorrect success prose — an override has to win outright.
                    content: replaceContent ? content : mergeFinalReply(entry.content, content) || entry.content,
                    streaming: false,
                    progressLabel: undefined,
                    chartCommandResults,
                    domain: 'chart',
                    showRetry,
                    retryPrompt: showRetry ? retryPrompt : undefined,
                })

                if (assistantMessageId) {
                    setMessages((prev) => prev.map((entry) => (entry.id === assistantMessageId ? patch(entry) : entry)))
                } else if (content || chartCommandResults) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: uuid(),
                            turnId: currentTurnIdRef.current ?? undefined,
                            role: 'assistant',
                            content: content || chatErrorText('NOT_UNDERSTOOD'),
                            chartCommandResults,
                            domain: 'chart',
                            showRetry: showRetry || !content,
                            retryPrompt: showRetry || !content ? retryPrompt : undefined,
                        },
                    ])
                }
                assistantIdRef.current = null
                assistantHadDeltasRef.current = false
                lastSeqRef.current = 0
                chartCommandsAppliedRef.current = false
                void refreshConversations()
            } catch (error) {
                reportChatError('AiAssistant', 'CHART_UPDATE_FAILED', error)
                const aid = assistantMessageId ?? assistantIdRef.current
                if (aid) {
                    setMessages((prev) =>
                        prev.map((entry) =>
                            entry.id === aid
                                ? {
                                      ...entry,
                                      streaming: false,
                                      progressLabel: undefined,
                                      showRetry: true,
                                      retryPrompt: lastUserQueryRef.current || undefined,
                                  }
                                : entry,
                        ),
                    )
                }
            } finally {
                clearRunInFlightState()
            }
        },
        [applyPendingChartCommands, cancelRunFinalizeWatchdog, clearRunInFlightState, mergeFinalReply, refreshConversations],
    )

    const scheduleRunFinalizeWatchdog = useCallback(() => {
        cancelRunFinalizeWatchdog()
        runFinalizeWatchdogRef.current = setTimeout(() => {
            runFinalizeWatchdogRef.current = null
            if (!loadingRef.current) return
            const aid = assistantIdRef.current
            if (aid) {
                setMessages((prev) =>
                    prev.map((entry) =>
                        entry.id === aid
                            ? { ...entry, streaming: false, progressLabel: undefined }
                            : entry,
                    ),
                )
            }
            clearRunInFlightState()
        }, 2500)
    }, [cancelRunFinalizeWatchdog, clearRunInFlightState])

    const commitPendingLedgerApply = useCallback(() => {
        const pending = screenerLedgerRef.current.pendingLedgerApply
        if (!pending) return
        const surface = getSurface('screener')
        if (!surface) {
            // The surface is only registered on the screener route. Keep the pending apply
            // so a later navigation can still land it rather than dropping the turn's
            // filters while the ledger advances as if they had been applied.
            console.warn('[AiAssistant] screener surface unavailable, filters not applied')
            setError(chatErrorText('SURFACE_UNAVAILABLE'))
            return
        }
        screenerLedgerRef.current.pendingLedgerApply = null
        ledgerRef.current = pending.ledgerAfter
        void surface.applyArtifacts({ filters: pending.ledgerAfter, mode: 'replace' })
    }, [])

    const restoreScreenerFromSurface = useCallback((filters: ScreenerFilterWithIdAndTag[]) => {
        const surface = getSurface('screener')
        void surface?.applyArtifacts({ filters, mode: 'replace' })
    }, [])

    const attachServerTurnId = useCallback((serverTurnId: string) => {
        currentTurnIdRef.current = serverTurnId
        const aid = assistantIdRef.current
        if (aid) {
            setMessages((prev) => prev.map((m) => (m.id === aid ? { ...m, turnId: serverTurnId } : m)))
        }
    }, [])

    const attachFiltersToAssistantMessage = useCallback(
        (assistantId: string, delta: ScreenerFilterWithIdAndTag[]) => {
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === assistantId
                        ? {
                              ...m,
                              filters: delta,
                              filterStatus: 'pending' as FilterCardStatus,
                              ...(currentTurnIdRef.current ? { turnId: currentTurnIdRef.current } : {}),
                          }
                        : m,
                ),
            )
        },
        [],
    )

    const markCurrentTurnFiltersApplied = useCallback((turnId: string | null | undefined) => {
        if (!turnId) return
        setMessages((prev) =>
            prev.map((m) =>
                m.turnId === turnId && m.filters?.length ? { ...m, filterStatus: 'applied' as FilterCardStatus } : m,
            ),
        )
    }, [])

    const ensureAssistantMessage = useCallback(
        (initialContent = ''): string => {
            if (assistantIdRef.current) return assistantIdRef.current
            const id = uuid()
            assistantIdRef.current = id
            assistantHadDeltasRef.current = false
            lastSeqRef.current = 0
            const isRoutingStub = routingAssistantActiveRef.current
            if (!isRoutingStub) {
                routingAssistantActiveRef.current = false
            }
            const buffered = screenerLedgerRef.current.pendingFiltersBuffer
            screenerLedgerRef.current.pendingFiltersBuffer = null
            const runDomain = pendingRunDomainRef.current ?? conversationDomain ?? undefined
            setMessages((prev) => {
                const base = isRoutingStub ? prev : prev.filter((m) => !m.routingOnly)
                return [
                    ...base,
                    {
                        id,
                        turnId: currentTurnIdRef.current ?? undefined,
                        role: 'assistant' as const,
                        content: initialContent,
                        streaming: true,
                        routingOnly: isRoutingStub,
                        domain: isRoutingStub ? undefined : runDomain,
                        ...(initialContent.trim()
                            ? {}
                            : { progressLabel: AI_CHAT_PROGRESS_PLACEHOLDER }),
                        ...(buffered?.length
                            ? { filters: buffered, filterStatus: 'pending' as FilterCardStatus }
                            : {}),
                    },
                ]
            })
            return id
        },
        [conversationDomain],
    )

    const applyStreamingContent = useCallback(
        (reply: string, seq?: number) => {
            if (!reply) return
            if (typeof seq === 'number') {
                if (seq <= lastSeqRef.current) return
                lastSeqRef.current = seq
            }
            assistantHadDeltasRef.current = true
            const aid = ensureAssistantMessage(typeof seq === 'number' ? reply : '')
            setMessages((prev) =>
                prev.map((m) => {
                    if (m.id !== aid) return m
                    const content = typeof seq === 'number' ? reply : m.content + reply
                    return {
                        ...m,
                        content,
                        streaming: true,
                        ...(content.trim() ? { progressLabel: undefined } : {}),
                    }
                }),
            )
        },
        [ensureAssistantMessage],
    )

    const onSendInternalRef = useRef<(text?: string, options?: { agent?: AiChatRequestMetadata['agent']; continuesTurn?: boolean }) => void>(() => {})

    const completeNavigationResend = useCallback((domain: AgentDomain, prompt: string) => {
        setConversationDomain(domain)
        pendingNavigationRef.current = null
        navAbortRef.current = null
        setError(null)
        setCurrentTaskId(null)
        activeTaskIdRef.current = null
        pendingRunDomainRef.current = domain
        routingAssistantActiveRef.current = false
        onSendInternalRef.current(prompt, { agent: domain, continuesTurn: true })
    }, [])

    const handleNavigationRequired = useCallback(
        async (domain: AgentDomain, prompt: string) => {
            navAbortRef.current?.abort()
            const abort = new AbortController()
            navAbortRef.current = abort
            const token = ++navigationTokenRef.current
            pendingNavigationRef.current = { domain, prompt }
            const ok = await navigateToDomain(domain)
            if (!ok || token !== navigationTokenRef.current) {
                pendingNavigationRef.current = null
                navAbortRef.current = null
                setError(chatErrorText('ROUTING_FAILED'))
                setLoading(false)
                return
            }
            try {
                // Preparation has to happen between mount and readiness: the screener only
                // reports ready once it is on the scratch screen, and `prepareForTurn` is
                // what moves it there. Waiting for readiness first would deadlock.
                const surface = await waitForSurfaceRegistered(domain, { signal: abort.signal })
                if (token !== navigationTokenRef.current) return
                if (domain === 'screener' && surface.prepareForTurn) {
                    const notice = await surface.prepareForTurn()
                    if (notice === false) {
                        pendingNavigationRef.current = null
                        navAbortRef.current = null
                        setLoading(false)
                        setError(chatErrorText('NAVIGATION_CANCELLED'))
                        return
                    }
                    if (notice) {
                        setMessages((prev) => [
                            ...prev,
                            { id: uuid(), role: 'assistant', content: notice },
                        ])
                    }
                }
                await waitForSurfaceReady(domain, { signal: abort.signal })
                if (token !== navigationTokenRef.current) return
                completeNavigationResend(domain, prompt)
            } catch (err) {
                if (abort.signal.aborted) {
                    pendingNavigationRef.current = null
                    navAbortRef.current = null
                    setLoading(false)
                    setError(chatErrorText('NAVIGATION_CANCELLED'))
                    return
                }
                pendingNavigationRef.current = null
                navAbortRef.current = null
                setLoading(false)
                setError(chatErrorText('SURFACE_UNAVAILABLE'))
                reportChatError('AiAssistant', 'ROUTING_FAILED', err)
            }
        },
        [completeNavigationResend, navigateToDomain],
    )

    useEffect(() => {
        const pending = pendingNavigationRef.current
        if (!pending) return
        const onTargetRoute =
            pending.domain === 'chart'
                ? matchRoute(location.pathname, [{ path: ROUTES.chart, key: 'chart' }])
                : matchRoute(location.pathname, [{ path: ROUTES.screener, key: 'screener' }])
        if (!onTargetRoute) {
            navAbortRef.current?.abort()
            pendingNavigationRef.current = null
            navAbortRef.current = null
            setLoading(false)
        }
    }, [location.pathname])

    const shouldIgnoreChatEvent = useCallback((chatEvent: AiChatEventMessage) => {
        if (
            chatEvent.conversation_id &&
            conversationIdRef.current &&
            chatEvent.conversation_id !== conversationIdRef.current
        ) {
            return true
        }
        const taskId = chatEvent.task_id?.trim()
        if (!taskId) return false
        if (ignoredTaskIdsRef.current.has(taskId)) return true
        if (activeTaskIdRef.current === null) {
            activeTaskIdRef.current = taskId
            return false
        }
        return taskId !== activeTaskIdRef.current
    }, [])

    const handleWsEvent = useCallback(
        (event: AiChatWsMessage) => {
            if (event.type === 'accepted') {
                if (
                    event.conversation_id &&
                    conversationIdRef.current &&
                    event.conversation_id !== conversationIdRef.current
                ) {
                    return
                }
                const serverTurnId = (event.metadata as Record<string, unknown> | undefined)?.turn_id
                if (typeof serverTurnId === 'string' && serverTurnId) {
                    attachServerTurnId(serverTurnId)
                }
                return
            }
            if (event.type !== 'chat_event') return
            const chatEvent = event as AiChatEventMessage
            if (shouldIgnoreChatEvent(chatEvent)) return

            const meta = (chatEvent.metadata ?? {}) as Record<string, unknown>
            const eventType = String(meta.event_type ?? '')

            const streamingArtifactPatch = tradingTurnArtifactPatchFromMetadata(meta)
            if (streamingArtifactPatch) {
                const aid = assistantIdRef.current ?? ensureAssistantMessage()
                setMessages((prev) =>
                    markSupersededOrderPreviews(
                        prev.map((m) =>
                            m.id === aid
                                ? {
                                      ...m,
                                      ...streamingArtifactPatch,
                                      domain: 'trading' as const,
                                      orderPreviewLoading: streamingArtifactPatch.orderPreview
                                          ? false
                                          : m.orderPreviewLoading,
                                  }
                                : m,
                        ),
                    ),
                )
            }

            if (chatEvent.task_id) {
                setCurrentTaskId(chatEvent.task_id)
            }

            if (eventType === 'RUN_STARTED' || chatEvent.status === 'started') {
                chartCommandsAppliedRef.current = false
                if (assistantIdRef.current) {
                    lastSeqRef.current = 0
                    assistantHadDeltasRef.current = false
                    const aid = assistantIdRef.current
                    const runDomain = pendingRunDomainRef.current ?? conversationDomain ?? undefined
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === aid
                                ? {
                                      ...m,
                                      content:
                                          m.routingOnly || isRoutingOnlyAssistantText(m.content)
                                              ? ''
                                              : m.content,
                                      streaming: true,
                                      routingOnly: false,
                                      domain: runDomain ?? m.domain,
                                      chartCommandResults: undefined,
                                      showRetry: false,
                                      retryPrompt: undefined,
                                  }
                                : m,
                        ),
                    )
                } else {
                    ensureAssistantMessage()
                }
                return
            }

            if (eventType === 'ROUTE_DECIDED') {
                const domain = String(meta.domain || '').toLowerCase()
                if (domain === 'chart' || domain === 'screener' || domain === 'trading') {
                    setConversationDomain(domain)
                }
            } else if (eventType === 'NAVIGATION_REQUIRED') {
                const domain = String(meta.domain || '').toLowerCase() as AgentDomain
                const prompt = lastUserQueryRef.current || ''
                routingAssistantActiveRef.current = true
                if ((domain === 'chart' || domain === 'screener') && prompt) {
                    void handleNavigationRequired(domain, prompt)
                }
                return
            } else if (eventType === 'CHART_COMMANDS_GENERATED') {
                pendingCommandsRef.current.push(...chartCommandsFromMetadata(meta))
                const targetId = targetChartIdFromMetadata(meta)
                if (targetId) targetChartIdRef.current = targetId
                setConversationDomain('chart')
                const aid = assistantIdRef.current ?? ensureAssistantMessage()
                void applyPendingChartCommands(aid)
            } else if (eventType === 'STEP_STARTED') {
                const name = String(meta.step_name ?? 'Working')
                const aid = assistantIdRef.current ?? ensureAssistantMessage()
                const orderProposalStep = /creating (?:order|modification|cancellation) proposal/i.test(name)
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === aid
                            ? {
                                  ...m,
                                  progressLabel: name,
                                  streaming: true,
                                  ...(orderProposalStep
                                      ? { domain: 'trading' as const, orderPreviewLoading: true }
                                      : {}),
                              }
                            : m,
                    ),
                )
            } else if (eventType === 'FILTERS_GENERATED') {
                setConversationDomain('screener')
                const surface = getSurface('screener')
                if (meta.clear_all === true) {
                    screenerLedgerRef.current.pendingLedgerApply = null
                    screenerLedgerRef.current.pendingFiltersBuffer = null
                    ledgerRef.current = []
                    void surface?.applyArtifacts({ clear_all: true })
                } else {
                    const parsed = parseFiltersFromProto(meta.filters)
                    if (parsed?.length) {
                        const pending = computePendingFilterUpdate(
                            ledgerRef.current,
                            meta,
                            parsed,
                        )
                        if (pending?.delta.length) {
                            const parsedTurnDelta = parseFiltersFromProto(meta.turn_delta)
                            const displayDelta = parsedTurnDelta?.length ? parsedTurnDelta : pending.delta
                            const replaceMode = meta.mode === 'replace'
                            if (replaceMode && surface) {
                                ledgerRef.current = pending.ledgerAfter
                                void surface.applyArtifacts({
                                    filters: pending.ledgerAfter,
                                    mode: 'replace',
                                    fields: meta.fields,
                                    sort: meta.sort,
                                })
                                screenerLedgerRef.current.pendingLedgerApply = null
                            } else {
                                // Also covers replace mode with no surface yet: defer rather
                                // than discard, so the commit step can retry after navigation.
                                screenerLedgerRef.current.pendingLedgerApply = pending
                            }
                            screenerLedgerRef.current.pendingFiltersBuffer = displayDelta
                            const aid = assistantIdRef.current
                            if (aid) {
                                attachFiltersToAssistantMessage(aid, displayDelta)
                                screenerLedgerRef.current.pendingFiltersBuffer = null
                            }
                        }
                    }
                }
            } else if (eventType === 'TEXT_MESSAGE_START') {
                if (assistantIdRef.current) {
                    lastSeqRef.current = 0
                    assistantHadDeltasRef.current = false
                    const buffered = screenerLedgerRef.current.pendingFiltersBuffer
                    screenerLedgerRef.current.pendingFiltersBuffer = null
                    if (buffered?.length) {
                        attachFiltersToAssistantMessage(assistantIdRef.current, buffered)
                    }
                    return
                }
                ensureAssistantMessage()
            } else if (eventType === 'TEXT_MESSAGE_CONTENT') {
                const seqRaw = meta.seq
                const seq = typeof seqRaw === 'number' ? seqRaw : Number(seqRaw)
                applyStreamingContent(chatEvent.reply || '', Number.isFinite(seq) ? seq : undefined)
            } else if (eventType === 'TEXT_MESSAGE_END') {
                const aid = assistantIdRef.current
                if (aid) {
                    setMessages((prev) =>
                        prev.map((m) => {
                            if (m.id !== aid) return m
                            const waitingPreview = m.orderPreviewLoading && !m.orderPreview
                            return {
                                ...m,
                                streaming: false,
                                progressLabel: waitingPreview ? 'Loading order preview' : undefined,
                            }
                        }),
                    )
                }
                scheduleRunFinalizeWatchdog()
            } else if (eventType === 'RUN_FINISHED' || chatEvent.status === 'completed') {
                cancelRunFinalizeWatchdog()
                if (!pendingNavigationRef.current && isRoutingOnlyAssistantText(chatEvent.reply)) {
                    const domain = routingDomainFromStubText(chatEvent.reply)
                    const prompt = lastUserQueryRef.current || ''
                    if (domain && prompt) {
                        routingAssistantActiveRef.current = true
                        void handleNavigationRequired(domain, prompt)
                        return
                    }
                }
                if (pendingCommandsRef.current.length === 0 && !chartCommandsAppliedRef.current) {
                    pendingCommandsRef.current.push(...chartCommandsFromNewMessages(meta))
                    const targetId = targetChartIdFromMetadata(meta)
                    if (targetId) targetChartIdRef.current = targetId
                }
                if (pendingNavigationRef.current) {
                    const assistantMessageId = assistantIdRef.current
                    if (assistantMessageId) {
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === assistantMessageId
                                    ? {
                                          ...m,
                                          streaming: false,
                                          routingOnly: isRoutingOnlyAssistantText(m.content) || m.routingOnly,
                                      }
                                    : m,
                            ),
                        )
                    }
                    setCurrentTaskId(null)
                    activeTaskIdRef.current = null
                    return
                }
                const assistantMessageId = assistantIdRef.current
                const runDomain = resolveTurnCompletionDomain({
                    metadata: meta,
                    conversationDomain,
                    pathname: location.pathname,
                    hasPendingChartCommands: pendingCommandsRef.current.length > 0,
                    chartCommandsAlreadyApplied: chartCommandsAppliedRef.current,
                })
                const completionSeqRaw = meta.seq
                const completionSeq =
                    typeof completionSeqRaw === 'number'
                        ? completionSeqRaw
                        : Number.isFinite(Number(completionSeqRaw))
                          ? Number(completionSeqRaw)
                          : undefined
                if (runDomain === 'chart') {
                    void finishChartRun({
                        assistantMessageId,
                        finalReply: chatEvent.reply?.trim(),
                    })
                } else {
                    const finishedTurnId = currentTurnIdRef.current
                    const finalReply = chatEvent.reply?.trim()
                    const completedArtifacts: TradingTurnArtifactPatch | null =
                        tradingTurnArtifactPatchFromMetadata(meta)
                    const completedPreview = completedArtifacts?.orderPreview ?? null
                    const completedInquiry = completedArtifacts?.orderInquiry ?? null
                    const completedPositionInquiry = completedArtifacts?.positionInquiry ?? null
                    const completedAccountFundsInquiry = completedArtifacts?.accountFundsInquiry ?? null
                    const isScreenerRun = runDomain === 'screener'
                    const orphanedFilters = isScreenerRun
                        ? screenerLedgerRef.current.pendingFiltersBuffer
                        : null
                    if (isScreenerRun) {
                        screenerLedgerRef.current.pendingFiltersBuffer = null
                    }
                    const messageId =
                        assistantMessageId ??
                        (finalReply ||
                        completedPreview ||
                        completedInquiry ||
                        completedPositionInquiry ||
                        completedAccountFundsInquiry
                            ? ensureAssistantMessage()
                            : null)
                    if (
                        messageId &&
                        (finalReply ||
                            completedPreview ||
                            completedInquiry ||
                            completedPositionInquiry ||
                            completedAccountFundsInquiry)
                    ) {
                        setMessages((prev) =>
                            markSupersededOrderPreviews(
                                prev.map((m) =>
                                    m.id === messageId
                                        ? {
                                              ...m,
                                              content: finalReply
                                                  ? mergeFinalReply(m.content, finalReply, completionSeq)
                                                  : m.content,
                                              streaming: false,
                                              progressLabel: undefined,
                                              ...(completedArtifacts
                                                  ? {
                                                        ...completedArtifacts,
                                                        domain: 'trading' as const,
                                                        orderPreviewLoading: false,
                                                    }
                                                  : {}),
                                          }
                                        : m,
                                ),
                            ),
                        )
                    } else if (orphanedFilters?.length) {
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: uuid(),
                                turnId: finishedTurnId ?? undefined,
                                role: 'assistant',
                                content: finalReply ?? '',
                                filters: orphanedFilters,
                                filterStatus: 'pending' as FilterCardStatus,
                            },
                        ])
                    }
                    if (isScreenerRun) {
                        commitPendingLedgerApply()
                        markCurrentTurnFiltersApplied(finishedTurnId)
                    }
                    assistantIdRef.current = null
                    assistantHadDeltasRef.current = false
                    lastSeqRef.current = 0
                    void refreshConversations()
                    clearRunInFlightState()
                }
            } else if (chatEvent.status === 'cancelled' || eventType === 'RUN_CANCELLED') {
                cancelRunFinalizeWatchdog()
                pendingCommandsRef.current = []
                targetChartIdRef.current = null
                chartCommandsAppliedRef.current = false
                const cancelledAssistantId = assistantIdRef.current
                if (cancelledAssistantId) {
                    setMessages((prev) =>
                        prev.map((entry) =>
                            entry.id === cancelledAssistantId
                                ? { ...entry, orderPreviewLoading: false, progressLabel: undefined, streaming: false }
                                : entry,
                        ),
                    )
                }
                assistantIdRef.current = null
                clearRunInFlightState()
            } else if (chatEvent.status === 'failed' || eventType === 'RUN_ERROR') {
                cancelRunFinalizeWatchdog()
                pendingCommandsRef.current = []
                targetChartIdRef.current = null
                chartCommandsAppliedRef.current = false
                const retryPrompt = lastUserQueryRef.current || undefined
                const failureText = chatErrorTextFromWire(meta.error_code)
                const assistantMessageId = assistantIdRef.current
                if (assistantMessageId) {
                    setMessages((prev) =>
                        prev.map((entry) => {
                            if (entry.id !== assistantMessageId) return entry
                            // The agent often streams a complete, correct answer before the run
                            // fails — declining an impossible request is the common case.
                            // Overwriting it with a generic error throws away the only useful
                            // thing in the turn, and Retry just re-runs into the same wall.
                            const streamed = entry.content?.trim() || ''
                            const keepStreamed = !!streamed && !isRoutingOnlyAssistantText(streamed)
                            return {
                                ...entry,
                                content: keepStreamed ? entry.content : failureText,
                                streaming: false,
                                progressLabel: undefined,
                                orderPreviewLoading: false,
                                showRetry: keepStreamed ? false : !!retryPrompt,
                                retryPrompt: keepStreamed ? undefined : retryPrompt,
                            }
                        }),
                    )
                } else {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: uuid(),
                            turnId: currentTurnIdRef.current ?? undefined,
                            role: 'assistant',
                            content: failureText,
                            showRetry: !!retryPrompt,
                            retryPrompt,
                        },
                    ])
                }
                setError(null)
                assistantIdRef.current = null
                clearRunInFlightState()
            }
        },
        [
            applyPendingChartCommands,
            applyStreamingContent,
            attachFiltersToAssistantMessage,
            attachServerTurnId,
            cancelRunFinalizeWatchdog,
            clearRunInFlightState,
            commitPendingLedgerApply,
            conversationDomain,
            ensureAssistantMessage,
            finishChartRun,
            handleNavigationRequired,
            location.pathname,
            markCurrentTurnFiltersApplied,
            mergeFinalReply,
            refreshConversations,
            scheduleRunFinalizeWatchdog,
            shouldIgnoreChatEvent,
        ],
    )

    useEffect(() => {
        if (!assistantEnabled || !ws) return
        const listener = (_data: Uint8Array, message: WebSocketResponseMessage) => {
            const event = decodeAiChatFromWsFrame(message)
            if (event) handleWsEvent(event)
        }
        ws.addListener(listener)
        return () => ws.removeListener(listener)
    }, [assistantEnabled, handleWsEvent, ws])

    const onSendInternal = useCallback(
        (textOverride?: string, options?: { agent?: AiChatRequestMetadata['agent']; continuesTurn?: boolean }) => {
            const text = (textOverride ?? input).trim()
            void (async () => {
                if (!assistantEnabled || !text || !ws) return
                if (loading && !options?.continuesTurn) return

                const token = await ensureValidAccessToken()
                if (!token) {
                    setError(chatErrorText('AUTH_EXPIRED'))
                    return
                }
                if (!ws) {
                    setError(chatErrorText('CONNECTION_LOST'))
                    return
                }

                // Created only once the turn is actually sendable, so a failed send never
                // leaves an empty conversation behind in history.
                let activeConversationId = conversationIdRef.current
                if (!activeConversationId) {
                    try {
                        const created = await createConversation()
                        activeConversationId = created.id
                        conversationIdRef.current = created.id
                        setConversationId(created.id)
                    } catch (e) {
                        const code = chatErrorCodeFromException(e, 'CHAT_START_FAILED')
                        setError(reportChatError('AiAssistant', code, e))
                        return
                    }
                }

                if (!textOverride) setInput('')
                setError(null)
                cancelRunFinalizeWatchdog()
                if (currentTaskId) {
                    ignoredTaskIdsRef.current.add(currentTaskId)
                }
                if (!options?.continuesTurn) {
                    assistantIdRef.current = null
                    assistantHadDeltasRef.current = false
                    lastSeqRef.current = 0
                    pendingCommandsRef.current = []
                    targetChartIdRef.current = null
                    chartCommandsAppliedRef.current = false
                    currentTurnIdRef.current = null
                    activeTaskIdRef.current = null
                } else {
                    activeTaskIdRef.current = null
                }
                lastUserQueryRef.current = text
                if (!options?.continuesTurn) {
                    const userId = uuid()
                    const assistantId = uuid()
                    assistantIdRef.current = assistantId
                    const runDomain = pendingRunDomainRef.current ?? conversationDomain ?? undefined
                    setMessages((prev) => [
                        ...prev,
                        { id: userId, role: 'user', content: text },
                        {
                            id: assistantId,
                            turnId: currentTurnIdRef.current ?? undefined,
                            role: 'assistant',
                            content: '',
                            streaming: true,
                            progressLabel: AI_CHAT_PROGRESS_PLACEHOLDER,
                            domain: runDomain,
                        },
                    ])
                } else if (assistantIdRef.current) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantIdRef.current
                                ? { ...m, streaming: true, progressLabel: AI_CHAT_PROGRESS_PLACEHOLDER }
                                : m,
                        ),
                    )
                } else {
                    const assistantId = uuid()
                    assistantIdRef.current = assistantId
                    const runDomain = pendingRunDomainRef.current ?? conversationDomain ?? undefined
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: assistantId,
                            turnId: currentTurnIdRef.current ?? undefined,
                            role: 'assistant',
                            content: '',
                            streaming: true,
                            progressLabel: AI_CHAT_PROGRESS_PLACEHOLDER,
                            domain: runDomain,
                        },
                    ])
                }
                setLoading(true)

                const agent = options?.agent ?? 'orchestrator'
                if (agent === 'chart' || agent === 'screener' || agent === 'trading') {
                    pendingRunDomainRef.current = agent
                }
                if (agent === 'screener') {
                    const surface = getSurface('screener')
                    if (surface?.prepareForTurn) {
                        await surface.prepareForTurn()
                    }
                }

                const metadata = buildRequestMetadata(agent, options?.continuesTurn)
                sendAiChatMessage(ws, token, activeConversationId, text, metadata)
            })().catch((e) => {
                const assistantId = assistantIdRef.current
                if (assistantId) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? {
                                      ...m,
                                      streaming: false,
                                      progressLabel: undefined,
                                      showRetry: true,
                                      retryPrompt: text,
                                  }
                                : m,
                        ),
                    )
                }
                setLoading(false)
                // Deliberately not routed through chatErrorCodeFromException: that maps any
                // TypeError to CONNECTION_LOST for fetch callers, which misreports a bug here
                // as a network problem.
                setError(reportChatError('AiAssistant', 'UPSTREAM_UNAVAILABLE', e))
            })
        },
        [assistantEnabled, buildRequestMetadata, cancelRunFinalizeWatchdog, conversationDomain, conversationId, currentTaskId, input, loading, ws],
    )

    useEffect(() => {
        onSendInternalRef.current = onSendInternal
    }, [onSendInternal])

    const onSend = onSendInternal

    useEffect(() => {
        if (seedPromptRef.current && !loading) {
            const prompt = seedPromptRef.current
            seedPromptRef.current = null
            onSendInternal(prompt)
        }
    }, [loading, onSendInternal, seedPromptTick])

    const openAssistant = useCallback(
        (options?: AiAssistantOpenOptions) => {
            if (!isAiAssistantEnabled(userDetails)) return
            commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.AI_ASSISTANT)
            if (options?.domain) {
                setConversationDomain(options.domain)
            }
            if (options?.forceNew) {
                resetConversationUi()
                conversationIdRef.current = null
                setConversationId(null)
            }
            if (options?.seedPrompt) {
                seedPromptRef.current = options.seedPrompt
                setSeedPromptTick((tick) => tick + 1)
            }
        },
        [dispatch, resetConversationUi, userDetails],
    )

    useEffect(() => {
        if (!assistantEnabled) {
            registerAiAssistantOpen(null)
            return
        }
        registerAiAssistantOpen(openAssistant)
        return () => registerAiAssistantOpen(null)
    }, [assistantEnabled, openAssistant])

    const onRetry = useCallback((prompt: string) => onSendInternal(prompt), [onSendInternal])

    const abortInFlight = useCallback(() => {
        void (async () => {
            if (!ws || !conversationId || !currentTaskId) return
            const token = await ensureValidAccessToken()
            if (!token) return
            sendAiChatAbort(ws, token, conversationId, currentTaskId)
            navigationTokenRef.current += 1
            navAbortRef.current?.abort()
            navAbortRef.current = null
            pendingNavigationRef.current = null
        })()
    }, [conversationId, currentTaskId, ws])

    const onAbort = abortInFlight

    const onNewChat = useCallback(async () => {
        const isCurrentChatEmpty = messagesRef.current.length === 0 && !loadingRef.current && !error
        if (isCurrentChatEmpty) {
            return
        }

        if (loading && conversationId && currentTaskId && ws) {
            abortInFlight()
        }
        resetConversationUi()
        conversationIdRef.current = null
        setConversationId(null)
    }, [abortInFlight, conversationId, currentTaskId, error, loading, resetConversationUi, ws])

    const restoreTurnsToMessages = useCallback(
        (turns: AiConversationTurnResponse[]) => {
            const collapsed = collapseNavigatedTurns(turns)
            const restored: ChatTurnMessage[] = []
            let lastDomain: AgentDomain | null = null
            collapsed.forEach((t) => {
                if (t.userQuery) {
                    restored.push({ id: uuid(), role: 'user', content: t.userQuery })
                }
                const domain =
                    (t.agentDomain === 'chart' ||
                        t.agentDomain === 'screener' ||
                        t.agentDomain === 'trading'
                        ? t.agentDomain
                        : deriveTurnDomain(t.artifacts)) ?? null
                if (domain) lastDomain = domain
                const parsedFilters = filtersFromArtifacts(t.artifacts)
                const orderPreview = orderPreviewFromArtifacts(t.artifacts)
                const orderInquiry = orderInquiryFromArtifacts(t.artifacts)
                const positionInquiry = positionInquiryFromArtifacts(t.artifacts)
                const holdingsInquiry = holdingsInquiryFromArtifacts(t.artifacts)
                const pnlInquiry = pnlInquiryFromArtifacts(t.artifacts)
                const tradeHistory = tradeHistoryFromArtifacts(t.artifacts)
                const accountFundsInquiry = accountFundsInquiryFromArtifacts(t.artifacts)
                const status = (t.status || '').toLowerCase()
                const hasText = !!t.assistantText
                const isTerminal = isTerminalTurnStatus(status)
                if (
                    hasText ||
                    isTerminal ||
                    parsedFilters ||
                    orderPreview ||
                    orderInquiry ||
                    positionInquiry ||
                    holdingsInquiry ||
                    pnlInquiry ||
                    tradeHistory ||
                    accountFundsInquiry
                ) {
                    let content = t.assistantText || ''
                    if (!content) {
                        if (status === 'cancelled') content = '(cancelled)'
                        else if (status === 'failed') content = chatErrorText('UPSTREAM_UNAVAILABLE')
                        else if (
                            !parsedFilters &&
                            !orderPreview &&
                            !orderInquiry &&
                            !positionInquiry &&
                            !holdingsInquiry &&
                            !pnlInquiry &&
                            !tradeHistory &&
                            !accountFundsInquiry
                        ) {
                            content = chatErrorText('NOT_UNDERSTOOD')
                        }
                    }
                    restored.push({
                        id: uuid(),
                        turnId: t.id,
                        role: 'assistant',
                        content,
                        domain: domain ?? undefined,
                        ...(parsedFilters
                            ? {
                                  filters: parsedFilters,
                                  filterStatus: filterStatusFromArtifacts(t.artifacts),
                              }
                            : {}),
                        ...(orderPreview ? { orderPreview } : {}),
                        ...(orderInquiry ? { orderInquiry } : {}),
                        ...(positionInquiry ? { positionInquiry } : {}),
                        ...(holdingsInquiry ? { holdingsInquiry } : {}),
                        ...(pnlInquiry ? { pnlInquiry } : {}),
                        ...(tradeHistory ? { tradeHistory } : {}),
                        ...(accountFundsInquiry ? { accountFundsInquiry } : {}),
                    })
                }
            })
            if (lastDomain) setConversationDomain(lastDomain)
            setMessages(markSupersededOrderPreviews(restored))
        },
        [],
    )

    /**
     * A reload drops the in-flight run's WebSocket subscription. Without re-binding to
     * the running task the worker's completion arrives with nothing listening, so its
     * chart commands are never applied even though the turn text is persisted — the
     * transcript then claims a change the chart never received.
     */
    const resumeInFlightTurnIfNeeded = useCallback((turns: AiConversationTurnResponse[]) => {
        if (!turns.length) return
        const last = turns[turns.length - 1]
        if (!last || !isInFlightTurnStatus(last.status || '')) return

        currentTurnIdRef.current = last.id
        if (last.taskId) {
            activeTaskIdRef.current = last.taskId
            setCurrentTaskId(last.taskId)
        }
        setLoading(true)
    }, [])

    const loadConversation = useCallback(
        async (targetId: string, onMissing?: () => void) => {
            try {
                const detail = await getConversation(targetId)
                if (!detail) {
                    if (onMissing) onMissing()
                    else setError(chatErrorText('CONVERSATION_GONE'))
                    return
                }
                conversationIdRef.current = detail.id
                setConversationId(detail.id)
                if (detail.activeConversationFilters != null) {
                    const ledger = applyScreenerServerLedger(
                        detail.activeConversationFilters,
                        restoreScreenerFromSurface,
                    )
                    ledgerRef.current = ledger
                }
                restoreTurnsToMessages(detail.turns ?? [])
                resumeInFlightTurnIfNeeded(detail.turns ?? [])
            } catch (e) {
                const code = chatErrorCodeFromException(e, 'CONVERSATION_LOAD_FAILED')
                setError(reportChatError('AiAssistant', code, e))
            }
        },
        [restoreScreenerFromSurface, restoreTurnsToMessages, resumeInFlightTurnIfNeeded],
    )

    const switchConversation = useCallback(
        async (targetId: string) => {
            if (targetId === conversationId && (messagesRef.current.length > 0 || loading)) {
                setHistoryOpen(false)
                return
            }
            resetConversationUi()
            setHistoryOpen(false)
            await loadConversation(targetId)
        },
        [conversationId, loadConversation, loading, resetConversationUi],
    )

    useEffect(() => {
        if (!assistantEnabled || rehydratedRef.current || !initialSession.conversationId) return
        rehydratedRef.current = true
        void loadConversation(initialSession.conversationId, () => {
            conversationIdRef.current = null
            setConversationId(null)
        })
    }, [assistantEnabled, initialSession.conversationId, loadConversation])

    const onUndoTurnFilters = useCallback(
        (messageId: string) => {
            const turn = messagesRef.current.find((m) => m.id === messageId)
            if (!turn?.turnId || !conversationId || undoInFlightRef.current) return
            undoInFlightRef.current = true
            setUndoInFlight(true)
            const ledgerBeforeUndo = ledgerRef.current
            void updateTurnArtifactStatus(conversationId, turn.turnId, 'filter', 'undone')
                .then((resp) => {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === messageId ? { ...m, filterStatus: 'undone' as FilterCardStatus } : m,
                        ),
                    )
                    ledgerRef.current = applyScreenerServerLedger(
                        resp.activeConversationFilters,
                        restoreScreenerFromSurface,
                    )
                })
                .catch((e) => {
                    applyScreenerServerLedger({ filters: { filters: ledgerBeforeUndo } }, restoreScreenerFromSurface)
                    setError(reportChatError('AiAssistant', 'FILTER_UNDO_FAILED', e))
                })
                .finally(() => {
                    undoInFlightRef.current = false
                    setUndoInFlight(false)
                })
        },
        [conversationId, restoreScreenerFromSurface],
    )

    const onOrderPreviewChange = useCallback((messageId: string, preview: OrderPreview) => {
        setMessages((prev) =>
            prev.map((message) => (message.id === messageId ? { ...message, orderPreview: preview } : message)),
        )
    }, [])

    const value = useMemo(
        (): AiAssistantContextValue => ({
            conversationId,
            messages,
            input,
            setInput,
            loading,
            error,
            conversations,
            historyOpen,
            setHistoryOpen,
            wsConnected,
            conversationDomain,
            onSend,
            onRetry,
            onAbort,
            onNewChat,
            switchConversation,
            refreshConversations,
            openAssistant,
            abortInFlight,
            onUndoTurnFilters,
            onOrderPreviewChange,
            undoInFlight,
            userName,
            userAvatarUrl,
            ledgerRef,
        }),
        [
            abortInFlight,
            conversationDomain,
            conversationId,
            conversations,
            error,
            historyOpen,
            input,
            loading,
            messages,
            onAbort,
            onNewChat,
            onOrderPreviewChange,
            onRetry,
            onSend,
            onUndoTurnFilters,
            openAssistant,
            refreshConversations,
            switchConversation,
            undoInFlight,
            userAvatarUrl,
            userName,
            wsConnected,
        ],
    )

    return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>
}

/** For cards that may also render outside the assistant panel; returns null without a provider. */
export function useAiAssistantOptional(): AiAssistantContextValue | null {
    return useContext(AiAssistantContext)
}

export function useAiAssistant(): AiAssistantContextValue {
    const ctx = useContext(AiAssistantContext)
    if (!ctx) {
        throw new Error('useAiAssistant must be used within AiAssistantProvider')
    }
    return ctx
}
